import { NO_SCHOOL_TEMPLATE_ID } from "utils/scheduleResolver";
import { getLocalDateKey, normalizeToLocalDate, parseTimeToMinutes } from "utils/dateTime";
import { getScheduleAutoAssignColor } from "utils/scheduleEditorUtils";

export const LEGACY_CONVERSION_TEMPLATE_PREFIX = "legacy-conversion";
export const LEGACY_CONVERSION_ASSIGNMENT_SOURCE = "legacyScheduleConversion";

/** True when a converted schedule name is letter/cycle-based (e.g. "A Day", "Week B - Monday"), not a weekday label. */
export const isLegacyLetterBasedScheduleLabel = (label = "") => {
  const trimmed = String(label || "").trim();
  if (!trimmed) return false;
  if (/^[A-N] Day$/i.test(trimmed)) return true;
  if (/^Week [A-N]\b/i.test(trimmed)) return true;
  if (/^[A-N](?:\/[A-N])+ -/i.test(trimmed)) return true;
  return false;
};

export const shouldShowLegacyScheduleNameNote = (patterns = []) =>
  (Array.isArray(patterns) ? patterns : []).some((pattern) =>
    isLegacyLetterBasedScheduleLabel(pattern?.label)
  );

const WEEKDAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
const LETTER_OPTIONS = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N"];

const addDays = (date, days) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

const isWeekday = (date) => {
  const day = date.getDay();
  return day !== 0 && day !== 6;
};

const normalizeNumber = (value, fallback = 1) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const getActiveLetters = (settings = {}) => {
  if (!settings?.multiWeek) return ["A"];
  const total = Math.max(1, Math.min(LETTER_OPTIONS.length, normalizeNumber(settings.numberOfWeeks, 1)));
  return LETTER_OPTIONS.slice(0, total);
};

const getClassLabel = (classItem = {}) =>
  String(classItem.className || classItem.period || classItem.name || "Class");

const slugify = (value = "") =>
  String(value || "schedule")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48) || "schedule";

const createScheduleBlockId = ({ patternId, classId, startTime, index }) =>
  `${patternId}-${String(classId || "class")}-${String(startTime || "time").replace(/:/g, "")}-${index}`;

const compareBlocks = (firstBlock, secondBlock) => {
  const firstStart = parseTimeToMinutes(firstBlock.startTime) ?? 0;
  const secondStart = parseTimeToMinutes(secondBlock.startTime) ?? 0;
  if (firstStart !== secondStart) return firstStart - secondStart;
  return String(firstBlock.classId || "").localeCompare(String(secondBlock.classId || ""));
};

const getPatternSignature = (blocks = []) =>
  blocks
    .map((block) => `${block.classId}|${block.startTime}|${block.endTime}`)
    .join("||");

const getEntryIssue = (entry, className = "This class") => {
  if (!entry?.active) return null;
  const startTime = typeof entry.startTime === "string" ? entry.startTime : "";
  const endTime = typeof entry.endTime === "string" ? entry.endTime : "";
  const startMinutes = parseTimeToMinutes(startTime);
  const endMinutes = parseTimeToMinutes(endTime);

  if (!startTime || !endTime) {
    return `${className} is marked active but is missing a start or end time, so we left it out.`;
  }

  if (startTime === "00:00" && endTime === "00:00") {
    return `${className} still has the default 12:00 AM time, so we left it out.`;
  }

  if (startMinutes === null || endMinutes === null) {
    return `${className} has a time we could not read, so we left it out.`;
  }

  if (endMinutes <= startMinutes) {
    return `${className} has an end time that is not after the start time, so we left it out.`;
  }

  return null;
};

const buildSourceEntry = ({ sourceKey, label, letter = "A", weekday = "Monday" }) => ({
  sourceKey,
  label,
  letter,
  weekday,
});

const getWeekCycleSourceLabel = (letter, weekday) => {
  if (weekday === "Tuesday" || weekday === "Thursday") {
    return `Week ${letter} - T/Th`;
  }

  if (weekday === "Monday" || weekday === "Wednesday" || weekday === "Friday") {
    return `Week ${letter} - M/W/F`;
  }

  return `Week ${letter} ${weekday}`;
};

const WEEKDAY_PRESET_LABELS = new Map([
  [JSON.stringify(["Monday", "Wednesday", "Friday"]), "M/W/F"],
  [JSON.stringify(["Tuesday", "Thursday"]), "T/Th"],
]);

const sortWeekdays = (weekdays = []) =>
  [...new Set(weekdays)].sort(
    (firstWeekday, secondWeekday) =>
      WEEKDAY_NAMES.indexOf(firstWeekday) - WEEKDAY_NAMES.indexOf(secondWeekday)
  );

const getCompactWeekdayLabel = (weekdays = []) => {
  const sortedWeekdays = sortWeekdays(weekdays);
  if (!sortedWeekdays.length) return "Schedule";
  if (sortedWeekdays.length === 1) return sortedWeekdays[0];

  const presetLabel = WEEKDAY_PRESET_LABELS.get(JSON.stringify(sortedWeekdays));
  if (presetLabel) return presetLabel;

  return sortedWeekdays
    .map((weekday) => {
      if (weekday === "Thursday") return "Th";
      return weekday.charAt(0);
    })
    .join("/");
};

const parseSourceKeyEntries = (sourceKeys = []) =>
  (Array.isArray(sourceKeys) ? sourceKeys : [])
    .map((sourceKey) => {
      const [letter = "A", weekday = "Monday"] = String(sourceKey).split(":");
      return { letter, weekday };
    })
    .filter(({ weekday }) => WEEKDAY_NAMES.includes(weekday));

const buildPatternLabelFromSourceKeys = ({ sourceKeys = [], userSettings = {} } = {}) => {
  const entries = parseSourceKeyEntries(sourceKeys);
  if (!entries.length) return "Schedule";

  const weekdays = entries.map((entry) => entry.weekday);
  const letters = [...new Set(entries.map((entry) => entry.letter))];
  const compactDays = getCompactWeekdayLabel(weekdays);

  if (userSettings?.multiWeek && userSettings?.cycleType === "week") {
    if (letters.length === 1) {
      return `Week ${letters[0]} - ${compactDays}`;
    }

    return `${letters.join("/")} - ${compactDays}`;
  }

  if (userSettings?.multiWeek && userSettings?.cycleType === "day" && letters.length === 1) {
    return `${letters[0]} Day`;
  }

  return compactDays;
};

const getSourceEntries = (settings = {}) => {
  const letters = getActiveLetters(settings);

  if (settings?.multiWeek && settings?.cycleType === "day") {
    return letters.map((letter) =>
      buildSourceEntry({
        sourceKey: `${letter}:Monday`,
        label: `${letter} Day`,
        letter,
        weekday: "Monday",
      })
    );
  }

  if (settings?.multiWeek && settings?.cycleType === "week") {
    return letters.flatMap((letter) =>
      WEEKDAY_NAMES.map((weekday) =>
        buildSourceEntry({
          sourceKey: `${letter}:${weekday}`,
          label: getWeekCycleSourceLabel(letter, weekday),
          letter,
          weekday,
        })
      )
    );
  }

  return WEEKDAY_NAMES.map((weekday) =>
    buildSourceEntry({
      sourceKey: `A:${weekday}`,
      label: `${weekday} Schedule`,
      letter: "A",
      weekday,
    })
  );
};

const findOverlappingBlockIds = (blocks = []) => {
  const sortedBlocks = [...blocks].sort(compareBlocks);
  const overlappingIds = new Set();

  for (let firstIndex = 0; firstIndex < sortedBlocks.length; firstIndex += 1) {
    const firstBlock = sortedBlocks[firstIndex];
    const firstStart = parseTimeToMinutes(firstBlock.startTime);
    const firstEnd = parseTimeToMinutes(firstBlock.endTime);
    if (firstStart === null || firstEnd === null) continue;

    for (let secondIndex = firstIndex + 1; secondIndex < sortedBlocks.length; secondIndex += 1) {
      const secondBlock = sortedBlocks[secondIndex];
      const secondStart = parseTimeToMinutes(secondBlock.startTime);
      const secondEnd = parseTimeToMinutes(secondBlock.endTime);
      if (secondStart === null || secondEnd === null) continue;
      if (secondStart >= firstEnd) break;

      if (firstStart < secondEnd && secondStart < firstEnd) {
        overlappingIds.add(firstBlock.id);
        overlappingIds.add(secondBlock.id);
      }
    }
  }

  return overlappingIds;
};

export const dedupeLegacyConversionWarnings = (warnings = []) => {
  const seen = new Set();
  const deduped = [];

  (Array.isArray(warnings) ? warnings : []).forEach((warning) => {
    if (!warning) return;
    const key = [
      warning.type || "",
      warning.classId || "",
      warning.message || "",
    ].join("\0");
    if (seen.has(key)) return;
    seen.add(key);
    deduped.push(warning);
  });

  return deduped;
};

const getBlockIssue = (block, className = "This class") =>
  getEntryIssue(
    {
      active: true,
      startTime: block?.startTime,
      endTime: block?.endTime,
    },
    className
  );

export const computePatternWarningsFromBlocks = (blocks = []) => {
  const warnings = [];
  const candidateBlocks = [];

  (Array.isArray(blocks) ? blocks : []).forEach((block, index) => {
    const className = String(block?.name || block?.className || "Class");
    const issue = getBlockIssue(block, className);
    if (issue) {
      warnings.push({
        type: "excluded-block",
        severity: "warning",
        classId: block?.classId || "",
        className,
        message: issue,
      });
      return;
    }

    candidateBlocks.push({
      ...block,
      id: block?.id || `block-${index}`,
    });
  });

  const overlappingIds = findOverlappingBlockIds(candidateBlocks);
  candidateBlocks.forEach((block) => {
    if (!overlappingIds.has(block.id)) return;
    warnings.push({
      type: "overlap",
      severity: "warning",
      classId: block.classId,
      className: block.name,
      message: `${block.name} overlaps another class in this schedule pattern, so we left it out.`,
    });
  });

  return warnings;
};

const scanSourceEntry = ({ classes = [], sourceEntry }) => {
  const candidateBlocks = [];
  const warnings = [];

  (Array.isArray(classes) ? classes : []).forEach((classItem) => {
    if (classItem?.active === false) return;

    const entry = classItem?.schedule?.[sourceEntry.letter]?.[sourceEntry.weekday];
    if (!entry?.active) return;

    const className = getClassLabel(classItem);
    const issue = getEntryIssue(entry, className);
    if (issue) {
      warnings.push({
        type: "excluded-block",
        severity: "warning",
        classId: classItem.classId || "",
        className: getClassLabel(classItem),
        sourceKey: sourceEntry.sourceKey,
        message: issue,
      });
      return;
    }

    candidateBlocks.push({
      id: `candidate-${sourceEntry.sourceKey}-${String(classItem.classId || candidateBlocks.length)}`,
      classId: String(classItem.classId || ""),
      name: getClassLabel(classItem),
      startTime: String(entry.startTime),
      endTime: String(entry.endTime),
    });
  });

  const overlappingIds = findOverlappingBlockIds(candidateBlocks);
  const blocks = candidateBlocks
    .filter((block) => {
      if (!overlappingIds.has(block.id)) return true;
      warnings.push({
        type: "overlap",
        severity: "warning",
        classId: block.classId,
        className: block.name,
        sourceKey: sourceEntry.sourceKey,
        message: `${block.name} overlaps another class in this schedule pattern, so we left it out.`,
      });
      return false;
    })
    .sort(compareBlocks);

  if (!blocks.length) {
    warnings.push({
      type: "empty-pattern",
      severity: "info",
      sourceKey: sourceEntry.sourceKey,
      message: `We did not find usable class times for ${sourceEntry.label}.`,
    });
  }

  return {
    ...sourceEntry,
    blocks,
    warnings,
  };
};

const buildPatternFromSources = ({ source, patternIndex, userSettings = {} }) => {
  const sourceKeys = Array.isArray(source.sourceKeys) ? source.sourceKeys : [source.sourceKey];
  const label = buildPatternLabelFromSourceKeys({ sourceKeys, userSettings });
  const patternId = `${LEGACY_CONVERSION_TEMPLATE_PREFIX}-${slugify(label)}-${patternIndex + 1}`;
  const blocks = source.blocks.map((block, blockIndex) => ({
    id: createScheduleBlockId({
      patternId,
      classId: block.classId,
      startTime: block.startTime,
      index: blockIndex,
    }),
    classId: block.classId,
    name: block.name,
    startTime: block.startTime,
    endTime: block.endTime,
  }));

  return {
    id: patternId,
    label,
    originalLabel: label,
    color: getScheduleAutoAssignColor(patternIndex),
    active: true,
    selected: blocks.length > 0,
    sourceKeys,
    sourceLabels: Array.isArray(source.sourceLabels) ? source.sourceLabels : [source.label],
    blocks,
    warnings: dedupeLegacyConversionWarnings(source.warnings),
  };
};

const getSingleScheduleLabel = (patterns = []) => {
  if (patterns.length !== 1) return patterns;
  return [{ ...patterns[0], label: "Regular Day", originalLabel: patterns[0].originalLabel || patterns[0].label }];
};

export const buildLegacyScheduleConversionDraft = ({ userSettings = {}, classes = [] } = {}) => {
  const sourceEntries = getSourceEntries(userSettings);
  const scannedSources = sourceEntries.map((sourceEntry) => scanSourceEntry({ classes, sourceEntry }));
  const patternsBySignature = new Map();
  const emptySources = [];

  scannedSources.forEach((source) => {
    if (!source.blocks.length) {
      emptySources.push(source);
      return;
    }

    const signature = getPatternSignature(source.blocks);
    if (!patternsBySignature.has(signature)) {
      patternsBySignature.set(signature, {
        ...source,
        sourceKeys: [source.sourceKey],
        sourceLabels: [source.label],
      });
      return;
    }

    const existing = patternsBySignature.get(signature);
    existing.sourceKeys.push(source.sourceKey);
    existing.sourceLabels.push(source.label);
    existing.warnings = dedupeLegacyConversionWarnings([...existing.warnings, ...source.warnings]);
  });

  const rawPatterns = Array.from(patternsBySignature.values())
    .map((source, patternIndex) => buildPatternFromSources({ source, patternIndex, userSettings }));
  const patterns = !userSettings?.multiWeek ? getSingleScheduleLabel(rawPatterns) : rawPatterns;

  return {
    mode: userSettings?.multiWeek
      ? (userSettings?.cycleType === "week" ? "weekCycle" : "dayCycle")
      : "singleSchedule",
    patterns,
    emptySources,
    warnings: dedupeLegacyConversionWarnings(scannedSources.flatMap((source) => source.warnings)),
    hasUsablePatterns: patterns.some((pattern) => pattern.blocks.length > 0),
  };
};

export const getPatternTimeSummary = (pattern = {}) => {
  const blocks = Array.isArray(pattern.blocks) ? [...pattern.blocks].sort(compareBlocks) : [];
  if (!blocks.length) {
    return { firstTime: "", lastTime: "" };
  }

  return {
    firstTime: blocks[0].startTime,
    lastTime: blocks[blocks.length - 1].endTime,
  };
};

export const patternToScheduleTemplate = (pattern = {}) => {
  const label = String(pattern.label || pattern.name || "Schedule");
  const id = String(pattern.id || `${LEGACY_CONVERSION_TEMPLATE_PREFIX}-${slugify(label)}`);

  return {
    id,
    label,
    name: label,
    color: pattern.color || getScheduleAutoAssignColor(0),
    scheduleKey: id,
    dayMode: "mondayOnly",
    active: true,
    classes: (Array.isArray(pattern.blocks) ? pattern.blocks : []).map((block) => ({
      id: String(block.id || ""),
      classId: block.classId ? String(block.classId) : null,
      name: String(block.name || block.className || "Class"),
      startTime: String(block.startTime || ""),
      endTime: String(block.endTime || ""),
    })),
  };
};

export const mergePatternFromScheduleTemplate = (pattern = {}, template = {}) => {
  const label = String(template.label || template.name || pattern.label || "Schedule");
  const blocks = (Array.isArray(template.classes) ? template.classes : []).map((block) => ({
    id: String(block.id || ""),
    classId: block.classId ? String(block.classId) : null,
    name: String(block.name || block.className || "Class"),
    startTime: String(block.startTime || ""),
    endTime: String(block.endTime || ""),
  }));

  return {
    ...pattern,
    label,
    color: template.color || pattern.color || getScheduleAutoAssignColor(0),
    blocks,
    warnings: dedupeLegacyConversionWarnings(computePatternWarningsFromBlocks(blocks)),
  };
};

const getPatternBySourceKey = (patterns = []) => {
  const map = new Map();
  patterns.forEach((pattern) => {
    (Array.isArray(pattern.sourceKeys) ? pattern.sourceKeys : []).forEach((sourceKey) => {
      map.set(sourceKey, pattern);
    });
  });
  return map;
};

const countIsoWeekOffset = (fromInput, toInput) => {
  const getStartOfIsoWeek = (input) => {
    const date = normalizeToLocalDate(input);
    const day = date.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    date.setDate(date.getDate() + diff);
    return date;
  };
  const from = getStartOfIsoWeek(fromInput);
  const to = getStartOfIsoWeek(toInput);
  const msPerWeek = 7 * 24 * 60 * 60 * 1000;
  return Math.round((to.getTime() - from.getTime()) / msPerWeek);
};

const countWeekdayOffset = (fromInput, toInput) => {
  const from = normalizeToLocalDate(fromInput);
  const to = normalizeToLocalDate(toInput);
  if (from.getTime() === to.getTime()) return 0;

  const step = from < to ? 1 : -1;
  const pointer = new Date(from);
  let offset = 0;

  while (pointer.getTime() !== to.getTime()) {
    pointer.setDate(pointer.getDate() + step);
    if (isWeekday(pointer)) {
      offset += step;
    }
  }

  return offset;
};

const mod = (value, divisor) => ((value % divisor) + divisor) % divisor;

const getWeekdayName = (date) =>
  date.toLocaleDateString("en-US", { weekday: "long" });

const findSourceKeyForDate = ({ userSettings = {}, date, anchorDate, anchorSourceKey, patterns = [] }) => {
  const anchorLetter = String(anchorSourceKey || "A:Monday").split(":")[0] || "A";
  const letters = getActiveLetters(userSettings);
  const total = Math.max(1, letters.length);
  const anchorIndex = Math.max(0, letters.indexOf(anchorLetter));

  if (userSettings?.multiWeek && userSettings?.cycleType === "week") {
    const weekOffset = countIsoWeekOffset(anchorDate, date);
    const letter = letters[mod(anchorIndex + weekOffset, total)] || "A";
    return `${letter}:${getWeekdayName(date)}`;
  }

  if (userSettings?.multiWeek && userSettings?.cycleType === "day") {
    const dayOffset = countWeekdayOffset(anchorDate, date);
    const letter = letters[mod(anchorIndex + dayOffset, total)] || "A";
    return `${letter}:Monday`;
  }

  const pattern = patterns[0];
  if (pattern?.label === "Regular Day") {
    return pattern.sourceKeys?.[0] || `A:${getWeekdayName(date)}`;
  }

  return `A:${getWeekdayName(date)}`;
};

export const buildLegacyConversionAssignmentPreview = ({
  userSettings = {},
  patterns = [],
  rangeStart = "",
  rangeEnd = "",
  firstSchoolDay = "",
  firstSchoolDaySourceKey = "",
  existingAssignments = [],
} = {}) => {
  const startDate = normalizeToLocalDate(rangeStart);
  const endDate = normalizeToLocalDate(rangeEnd);
  const anchorDate = normalizeToLocalDate(firstSchoolDay || rangeStart);
  const selectedPatterns = (Array.isArray(patterns) ? patterns : []).filter((pattern) => pattern?.selected !== false);
  const patternBySourceKey = getPatternBySourceKey(selectedPatterns);
  const noSchoolDateKeys = new Set(
    (Array.isArray(existingAssignments) ? existingAssignments : [])
      .filter((assignment) => assignment?.active !== false && assignment?.templateId === NO_SCHOOL_TEMPLATE_ID)
      .map((assignment) => assignment?.date || assignment?.startDate)
      .filter(Boolean)
  );
  const rows = [];
  const skipped = {
    weekends: 0,
    noSchool: 0,
    missingPattern: 0,
  };

  if (!rangeStart || !rangeEnd || startDate > endDate || !selectedPatterns.length) {
    return { rows, skipped, countsByPatternId: {} };
  }

  for (let pointer = new Date(startDate); pointer <= endDate; pointer = addDays(pointer, 1)) {
    const dateKey = getLocalDateKey(pointer);
    if (!isWeekday(pointer)) {
      skipped.weekends += 1;
      continue;
    }

    if (noSchoolDateKeys.has(dateKey)) {
      skipped.noSchool += 1;
      continue;
    }

    const sourceKey = findSourceKeyForDate({
      userSettings,
      date: pointer,
      anchorDate,
      anchorSourceKey: firstSchoolDaySourceKey,
      patterns: selectedPatterns,
    });
    const pattern = patternBySourceKey.get(sourceKey);
    if (!pattern) {
      skipped.missingPattern += 1;
      continue;
    }

    rows.push({
      dateKey,
      templateId: pattern.id,
      patternId: pattern.id,
      patternLabel: pattern.label,
      sourceKey,
    });
  }

  return {
    rows,
    skipped,
    countsByPatternId: rows.reduce((counts, row) => ({
      ...counts,
      [row.patternId]: (counts[row.patternId] || 0) + 1,
    }), {}),
  };
};

const isAssignmentInRange = (assignment = {}, rangeStart = "", rangeEnd = "") => {
  const dateKey = assignment.date || assignment.startDate;
  if (!dateKey || !rangeStart || !rangeEnd) return false;
  return rangeStart <= dateKey && dateKey <= rangeEnd;
};

export const composeLegacyConversionScheduleCalendar = ({
  scheduleCalendar = {},
  selectedPatterns = [],
  previewRows = [],
  schoolYearSettings = null,
  rangeStart = "",
  rangeEnd = "",
} = {}) => {
  const existingTemplates = Array.isArray(scheduleCalendar?.templates) ? scheduleCalendar.templates : [];
  const existingAssignments = Array.isArray(scheduleCalendar?.assignments) ? scheduleCalendar.assignments : [];
  const nextTemplateIds = new Set(selectedPatterns.map((pattern) => pattern.id));
  const existingWithoutGeneratedTemplates = existingTemplates.filter(
    (template) => !String(template?.id || "").startsWith(`${LEGACY_CONVERSION_TEMPLATE_PREFIX}-`)
  );
  const conversionTemplates = selectedPatterns.map(patternToScheduleTemplate);
  const previewDateKeys = new Set(previewRows.map((row) => row.dateKey).filter(Boolean));
  const nextAssignments = [
    ...existingAssignments.filter((assignment) => {
      const assignmentDateKey = assignment?.date || assignment?.startDate;
      if (rangeStart && rangeEnd && isAssignmentInRange(assignment, rangeStart, rangeEnd)) {
        return assignment?.templateId === NO_SCHOOL_TEMPLATE_ID;
      }
      if (!previewDateKeys.has(assignmentDateKey)) return true;
      return assignment?.templateId === NO_SCHOOL_TEMPLATE_ID;
    }),
    ...previewRows
      .filter((row) => row?.dateKey && nextTemplateIds.has(row.templateId))
      .map((row) => ({
        id: `assignment-${row.dateKey}`,
        date: row.dateKey,
        templateId: row.templateId,
        active: true,
        source: LEGACY_CONVERSION_ASSIGNMENT_SOURCE,
      })),
  ];

  return {
    ...(scheduleCalendar || {}),
    scheduleRuntimeMode: "calendar",
    ...(schoolYearSettings ? { schoolYearSettings } : {}),
    templates: [
      ...existingWithoutGeneratedTemplates,
      ...conversionTemplates,
    ],
    assignments: nextAssignments,
  };
};

const getNoSchoolAssignments = (assignments = []) =>
  (Array.isArray(assignments) ? assignments : []).filter(
    (assignment) => assignment?.templateId === NO_SCHOOL_TEMPLATE_ID
  );

export const composeTemplatesOnlyCalendarSetup = ({
  scheduleCalendar = {},
  selectedPatterns = [],
  schoolYearSettings = null,
} = {}) => {
  const existingTemplates = Array.isArray(scheduleCalendar?.templates) ? scheduleCalendar.templates : [];
  const nextTemplateIds = new Set(selectedPatterns.map((pattern) => pattern.id));
  const existingWithoutGeneratedTemplates = existingTemplates.filter(
    (template) => !nextTemplateIds.has(template?.id)
      && !String(template?.id || "").startsWith(`${LEGACY_CONVERSION_TEMPLATE_PREFIX}-`)
  );

  return {
    ...(scheduleCalendar || {}),
    scheduleRuntimeMode: "calendar",
    ...(schoolYearSettings ? { schoolYearSettings } : {}),
    templates: [
      ...existingWithoutGeneratedTemplates,
      ...selectedPatterns.map(patternToScheduleTemplate),
    ],
    assignments: getNoSchoolAssignments(scheduleCalendar?.assignments),
  };
};

export const composeManualCalendarSetup = ({
  scheduleCalendar = {},
  schoolYearSettings = null,
} = {}) => ({
  ...(scheduleCalendar || {}),
  scheduleRuntimeMode: "calendar",
  ...(schoolYearSettings ? { schoolYearSettings } : {}),
  templates: Array.isArray(scheduleCalendar?.templates) ? scheduleCalendar.templates : [],
  assignments: getNoSchoolAssignments(scheduleCalendar?.assignments),
});

export const filterAssignmentsOutsideRangeOrNoSchool = ({
  assignments = [],
  rangeStart = "",
  rangeEnd = "",
} = {}) =>
  (Array.isArray(assignments) ? assignments : []).filter(
    (assignment) =>
      !isAssignmentInRange(assignment, rangeStart, rangeEnd)
      || assignment?.templateId === NO_SCHOOL_TEMPLATE_ID
  );
